import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jobsAPI } from '@/api';
import { QUERY_KEYS } from '@/utils/constants';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Clock, PlayCircle, CheckCircle, XCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { JobStatus } from '@/types';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

export default function JobMonitoringPage() {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: QUERY_KEYS.JOBS_STATUS,
    queryFn: jobsAPI.getStatus,
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  const triggerStaleTiersMutation = useMutation({
    mutationFn: jobsAPI.triggerStaleTiers,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS_STATUS });
      toast.success(response.message || 'Stale tier job triggered successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to trigger stale tier job');
    },
  });

  const triggerAtRiskMutation = useMutation({
    mutationFn: jobsAPI.triggerAtRisk,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS_STATUS });
      toast.success(response.message || 'At-risk job triggered successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to trigger at-risk job');
    },
  });

  const triggerSAAssignmentMutation = useMutation({
    mutationFn: jobsAPI.triggerSAAssignment,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS_STATUS });
      toast.success(response.message || 'SA assignment job triggered successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to trigger SA assignment job');
    },
  });

  const triggerWelcomeMessagesMutation = useMutation({
    mutationFn: jobsAPI.triggerWelcomeMessages,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS_STATUS });
      toast.success(response.message || 'Welcome messages job triggered successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to trigger welcome messages job');
    },
  });

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  const getStatusBadge = (job: JobStatus) => {
    if (job.isRunning) {
      return <Badge variant="default" className="bg-blue-500"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Running</Badge>;
    }

    if (!job.lastRun) {
      return <Badge variant="secondary"><AlertCircle className="w-3 h-3 mr-1" />Not Run Yet</Badge>;
    }

    if (job.lastRunSummary?.errors && job.lastRunSummary.errors.length > 0) {
      return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Errors</Badge>;
    }

    return <Badge variant="default" className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Success</Badge>;
  };

  const renderJobCard = (
    job: JobStatus,
    triggerMutation: typeof triggerStaleTiersMutation | typeof triggerAtRiskMutation
  ) => (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl">{job.name}</CardTitle>
            <CardDescription className="flex items-center gap-2 mt-1">
              <Clock className="w-4 h-4" />
              {job.schedule}
            </CardDescription>
          </div>
          {getStatusBadge(job)}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Last Run Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Last Run</p>
            <p className="font-medium">{formatDate(job.lastRun)}</p>
          </div>
          {job.lastRunSummary && (
            <div>
              <p className="text-sm text-muted-foreground">Duration</p>
              <p className="font-medium">{formatDuration(job.lastRunSummary.duration)}</p>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        {job.lastRunSummary && (
          <div className="space-y-3">
            {job.lastRunSummary.campusesProcessed !== undefined && (
              <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                <span className="text-sm">Campuses Processed</span>
                <span className="font-semibold">{job.lastRunSummary.campusesProcessed}</span>
              </div>
            )}

            {job.lastRunSummary.totalStudentsProcessed !== undefined && (
              <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                <span className="text-sm">Students Processed</span>
                <span className="font-semibold">{job.lastRunSummary.totalStudentsProcessed.toLocaleString()}</span>
              </div>
            )}

            {/* Welcome Messages Stats */}
            {(job.lastRunSummary as any).processed !== undefined && (
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                  <p className="text-xs text-muted-foreground">Processed</p>
                  <p className="font-semibold text-blue-700 dark:text-blue-400">
                    {(job.lastRunSummary as any).processed}
                  </p>
                </div>
                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                  <p className="text-xs text-muted-foreground">Sent</p>
                  <p className="font-semibold text-green-700 dark:text-green-400">
                    {(job.lastRunSummary as any).sent}
                  </p>
                </div>
                <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                  <p className="text-xs text-muted-foreground">Skipped</p>
                  <p className="font-semibold text-yellow-700 dark:text-yellow-400">
                    {(job.lastRunSummary as any).skipped}
                  </p>
                </div>
                <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded">
                  <p className="text-xs text-muted-foreground">Failed</p>
                  <p className="font-semibold text-red-700 dark:text-red-400">
                    {(job.lastRunSummary as any).failed}
                  </p>
                </div>
              </div>
            )}

            {/* SA Assignment Stats */}
            {(job.lastRunSummary as any).totalStudentsFound !== undefined && (
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                  <p className="text-xs text-muted-foreground">Found</p>
                  <p className="font-semibold text-blue-700 dark:text-blue-400">
                    {(job.lastRunSummary as any).totalStudentsFound}
                  </p>
                </div>
                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                  <p className="text-xs text-muted-foreground">Assigned</p>
                  <p className="font-semibold text-green-700 dark:text-green-400">
                    {(job.lastRunSummary as any).totalStudentsAssigned}
                  </p>
                </div>
                <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                  <p className="text-xs text-muted-foreground">Skipped</p>
                  <p className="font-semibold text-yellow-700 dark:text-yellow-400">
                    {(job.lastRunSummary as any).totalStudentsSkipped}
                  </p>
                </div>
              </div>
            )}

            {/* Tier Breakdown */}
            {job.lastRunSummary.tierBreakdown && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Tier Breakdown</p>
                <div className="grid grid-cols-2 gap-2">
                  {job.lastRunSummary.tierBreakdown.active !== undefined && (
                    <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                      <p className="text-xs text-muted-foreground">Active</p>
                      <p className="font-semibold text-green-700 dark:text-green-400">
                        {job.lastRunSummary.tierBreakdown.active.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {job.lastRunSummary.tierBreakdown.stale7Days !== undefined && (
                    <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                      <p className="text-xs text-muted-foreground">7 Days</p>
                      <p className="font-semibold text-yellow-700 dark:text-yellow-400">
                        {job.lastRunSummary.tierBreakdown.stale7Days.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {job.lastRunSummary.tierBreakdown.stale15Days !== undefined && (
                    <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded">
                      <p className="text-xs text-muted-foreground">15 Days</p>
                      <p className="font-semibold text-orange-700 dark:text-orange-400">
                        {job.lastRunSummary.tierBreakdown.stale15Days.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {job.lastRunSummary.tierBreakdown.stale30Days !== undefined && (
                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded">
                      <p className="text-xs text-muted-foreground">30+ Days</p>
                      <p className="font-semibold text-red-700 dark:text-red-400">
                        {job.lastRunSummary.tierBreakdown.stale30Days.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Errors */}
            {job.lastRunSummary.errors && job.lastRunSummary.errors.length > 0 && (
              <div className="p-3 bg-destructive/10 rounded-lg">
                <p className="text-sm font-medium text-destructive mb-2">Errors ({job.lastRunSummary.errors.length})</p>
                <ul className="text-xs space-y-1">
                  {job.lastRunSummary.errors.map((error, index) => (
                    <li key={index} className="text-muted-foreground">• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Trigger Button */}
        <Button
          onClick={() => triggerMutation.mutate()}
          disabled={job.isRunning || triggerMutation.isPending}
          className="w-full"
        >
          {triggerMutation.isPending ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Triggering...
            </>
          ) : (
            <>
              <PlayCircle className="w-4 h-4 mr-2" />
              Trigger Job Manually
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isError) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6">
          <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <XCircle className="w-12 h-12 text-destructive" />
            <p className="text-muted-foreground">Failed to load job status</p>
            <p className="text-sm text-destructive">{(error as any)?.message || 'Unknown error'}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Background Jobs Monitoring</h1>
          <p className="text-muted-foreground mt-1">
            Monitor and manage scheduled background jobs
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Server Time</p>
          <p className="font-medium">{formatDate(data?.serverTime || '')}</p>
        </div>
      </div>

      {/* Jobs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {data?.staleTiers && renderJobCard(data.staleTiers, triggerStaleTiersMutation)}
        {data?.atRisk && renderJobCard(data.atRisk, triggerAtRiskMutation)}
        {data?.saAssignment && renderJobCard(data.saAssignment, triggerSAAssignmentMutation)}
        {data?.welcomeMessages && renderJobCard(data.welcomeMessages, triggerWelcomeMessagesMutation)}
      </div>

      {/* Info Card */}
      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="text-sm">ℹ️ About Job Monitoring</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>• Jobs run automatically on their scheduled intervals</p>
          <p>• This page auto-refreshes every 30 seconds</p>
          <p>• Manual triggers are useful for testing or catching up after server restarts</p>
          <p>• Check errors section if jobs fail to complete successfully</p>
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
}
